import { calculateServiceFee, getServiceFeeRate } from '@/utils';
import { DEFERA_CAPITAL_HOLD_FEE_RATE, SLICE_PAY_HOLD_FEE_RATE } from '@/utils/paymentScheduleCalculator';
import { insurancePremiumCents, withoutInsurancePremium } from '../../supabase/functions/_shared/trip-insurance.js';
import { retainedExtensionFeesCents } from '@/utils/paymentExtensionChoices';

/**
 * Derive the customer-facing price breakdown for a booking the same way the
 * customer-facing modal (FlightDetailsModal.jsx) does.
 *
 * Why this helper exists: bookings created via Defera Capital / Defera Zero
 * write `total_amount = grand_total = grand_total_with_hold = the customer's
 * gross total` (i.e. base + service + hold). The earlier code in this file
 * naively treated `total_amount` as the base fare, which inflated the displayed
 * Base Fare by ~24%, produced bogus 6%/12% rate labels (instead of 8%/15%),
 * and rendered a fake "Total Price" that didn't match what the customer
 * actually paid Stripe.
 *
 * Derivation strategy (matches FlightDetailsModal):
 *   1. Back the base out of stored fees + Stripe-charged total:
 *      `grand_total_with_hold - service_fee - hold_fee`. This is the source of
 *      truth — the customer paid `grand_total_with_hold` to Stripe, so any
 *      breakdown the backer/investor sees must sum to that number, and the
 *      derived rates must match the documented service + hold tiers (current
 *      schedule: 8% / 30% / 40% service + 15%/17% hold; legacy 10% / 8% /
 *      40% / 30% schedule on pre-2026-05-19 bookings is still in-book).
 *      Used whenever any stored fee is present alongside a stored total.
 *   2. Fall back to `flight_data.base_amount` (the Duffel airline base). Only
 *      used when stored fees are missing — `base_amount` is the *original*
 *      Duffel base and is NOT updated when a booking is later modified (admin
 *      edits, flight changes, extras). Preferring it over the back-derived
 *      base on modified rows leaves the line items not adding up (e.g. T3ZCJX:
 *      stored fees imply base $1,020, but stale `base_amount` was $885 — old
 *      logic showed Base $885 / Service 9% / Hold 17% / Total $1,266.84 which
 *      didn't sum).
 *   3. Defera Zero special case retained from previous logic.
 *   4. As a last resort fall back to `total_amount` as the base.
 *
 * Returned `serviceFeeRate` / `holdFeeRate` are computed against the back-derived
 * base (and pre-hold subtotal), so they render the documented tiered rates
 * instead of derived-from-gross artifacts.
 *
 * NOTE: this also drives backer earnings (see `tripsWithEarnings` ~L413). On
 * trips that were modified post-creation, backers will now earn against the
 * current base, not the stale Duffel `base_amount`.
 *
 * FAYE TRAVEL INSURANCE (migration 156): a premium rides inside the booking's
 * stored totals, so back-deriving the base from them would hand backers a
 * share of it. Everything here is derived from the TRAVEL totals (premium
 * taken out first), so base fare, fees, `totalWithHold`, miles and earnings
 * are exactly what they would be without insurance. The premium is returned
 * on its own (`insurancePremium`), and `planTotal` is what the customer pays
 * in all (travel + premium).
 */
export function deriveBookingPricing(rawTrip) {
  const trip = withoutInsurancePremium(rawTrip || {});
  const insurancePremium = insurancePremiumCents(rawTrip) / 100;
  const flightData = trip.flight_data || trip.flightData || {};
  const paymentMethod =
    flightData._payment_method ||
    flightData.payment_method ||
    trip.payment_method ||
    flightData.booking_option ||
    'installments';
  const isMilesOnly = paymentMethod === 'miles';
  const isReserve = paymentMethod === 'reserve';
  const isDeferaZero = paymentMethod === 'defera_zero';
  const isDeferaCapital = paymentMethod === 'defera_capital';

  const hasStayData = !!(
    trip.stay_data ||
    trip.stays?.length ||
    flightData.stay_data
  );
  const isFlightHotel =
    !!(flightData._is_flight_hotel_package || flightData.is_flight_hotel_package) ||
    hasStayData;
  const hasPromo = !!flightData.promo_applied;

  const storedServiceFee = parseFloat(trip.service_fee ?? trip.serviceFee ?? 0) || 0;
  const storedHoldFee = parseFloat(
    trip.hold_fee ?? trip.holdFee ?? flightData.hold_fee ?? 0
  ) || 0;
  const storedTotalAmount = parseFloat(
    trip.totalPrice ?? trip.total_amount ?? trip.total_price ?? 0
  ) || 0;
  const storedGrandTotal = parseFloat(trip.grand_total ?? trip.grandTotal ?? 0) || 0;
  const storedGrandWithHold = parseFloat(
    trip.grand_total_with_hold ??
      storedGrandTotal ??
      storedTotalAmount ??
      0
  ) || 0;
  /** An Extension Fee (moving a payment) sits inside the stored totals but is
   *  neither airfare nor a backer's base: back it out before deriving the base. */
  const extensionFees = retainedExtensionFeesCents(flightData) / 100;
  const flightDataBase = parseFloat(flightData.base_amount ?? 0) || 0;

  // Derive base fare. Order of preference:
  //   1. grand_total_with_hold - service_fee - hold_fee  (back-derived from stored
  //      fees + the customer's Stripe-charged total). This guarantees the modal's
  //      line items sum to what the customer actually paid, and that the displayed
  //      rates match the documented service/hold tiers (current: 8%/30%/40%
  //      service, 15%/17% hold; legacy pre-2026-05-19 had a 10% mid-tier).
  //   2. flight_data.base_amount  (Duffel airline base — used as fallback when stored
  //      fees are missing). NOT preferred over (1): `base_amount` is the *original*
  //      Duffel base and isn't updated when a booking is later modified (admin edits,
  //      flight changes, etc.), so a stale value here will silently make the line
  //      items not add up. See FlightDetailsModal.jsx (Slice Pay / Defera Capital
  //      branch around line 1125) — it uses the same back-derive logic for the
  //      customer-facing breakdown, and this helper must match so the backer/investor
  //      view shows the same numbers the customer sees.
  //   3. Defera Zero special case retained from previous logic.
  //   4. Fall back to total_amount as base (legacy / unpriced offers).
  const canBackDeriveFromStored =
    storedGrandWithHold > 0 && (storedServiceFee > 0 || storedHoldFee > 0);
  let baseFare;
  if (canBackDeriveFromStored) {
    baseFare = Math.max(0, storedGrandWithHold - extensionFees - storedServiceFee - storedHoldFee);
  } else if (flightDataBase > 0) {
    baseFare = flightDataBase;
  } else if (isDeferaZero && storedGrandTotal > 0) {
    baseFare = Math.max(0, storedGrandTotal - extensionFees - storedServiceFee - storedHoldFee);
  } else {
    baseFare = storedTotalAmount;
  }

  // Service fee (waived when promo or flight+hotel package).
  let serviceFee;
  let serviceFeeRate;
  if (isFlightHotel || hasPromo) {
    serviceFee = 0;
    serviceFeeRate = 0;
  } else if (storedServiceFee > 0 && baseFare > 0) {
    serviceFee = storedServiceFee;
    serviceFeeRate = serviceFee / baseFare;
  } else if (isReserve || isDeferaZero) {
    serviceFeeRate = 0.11;
    serviceFee = baseFare * 0.11;
  } else {
    serviceFeeRate = getServiceFeeRate(baseFare, isDeferaCapital);
    serviceFee = calculateServiceFee(baseFare, isDeferaCapital);
  }

  const preHoldSubtotal = baseFare + serviceFee;

  // Hold fee — use stored value when present; otherwise fall back to the
  // documented rate for the payment method (Reserve has no hold fee).
  let holdFee;
  if (isReserve) {
    holdFee = 0;
  } else if (storedHoldFee > 0) {
    holdFee = storedHoldFee;
  } else {
    holdFee = preHoldSubtotal * (isDeferaCapital ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE);
  }

  // Hold rate displayed as % of pre-hold subtotal (matches the documented
  // 15% tier, not "12%" computed against the post-hold total).
  const holdFeeRate = preHoldSubtotal > 0
    ? holdFee / preHoldSubtotal
    : (isReserve
        ? 0
        : (isDeferaCapital ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE));

  // Total the customer actually paid. Prefer the stored gross when present
  // (so the displayed Total exactly matches the Stripe charge); otherwise sum
  // the line items.
  let totalWithHold;
  if (storedGrandWithHold > 0) {
    totalWithHold = storedGrandWithHold;
  } else if (storedTotalAmount > 0 && Math.abs(storedTotalAmount - (baseFare + serviceFee + holdFee)) < 1) {
    totalWithHold = storedTotalAmount;
  } else {
    totalWithHold = preHoldSubtotal + holdFee;
  }

  return {
    paymentMethod,
    isMilesOnly,
    isReserve,
    isDeferaZero,
    isDeferaCapital,
    isFlightHotel,
    hasPromo,
    baseFare,
    serviceFee,
    serviceFeeRate,
    holdFee,
    holdFeeRate,
    preHoldSubtotal,
    totalWithHold,
    // Faye premium (pass-through, collected for Faye) and the customer's whole plan.
    insurancePremium,
    planTotal: insurancePremium > 0 ? totalWithHold + insurancePremium : totalWithHold,
  };
}
